/**
 * Graficos como `Scene`.
 *
 * Nao usamos biblioteca de graficos de proposito: o distrator "erro de leitura do
 * eixo" exige controle do eixo ao pixel, e uma lib briga com isso. Um grafico aqui e
 * so mais uma cena, entao herda tema, impressao e snapshot-test de graca.
 */

import { formatBR } from '../core/num.ts';
import { conta, de } from '../core/pt.ts';
import type { Vec2 } from '../geom/vec.ts';
import type { Scene, Shape } from './scene.ts';
import { scene } from './scene.ts';

export interface HistogramClass {
  readonly lo: number;
  readonly hi: number;
  readonly freq: number;
  /**
   * Frequência não informada.
   *
   * Existe porque uma barra de altura zero LÊ COMO "frequência zero", não como
   * "desconhecida" — e a diferença entre as duas leituras muda completamente o
   * problema. A classe desconhecida é desenhada tracejada, com "?".
   */
  readonly unknown?: boolean;
}

export interface HistogramOptions {
  readonly classes: readonly HistogramClass[];
  readonly xLabel: string;
  readonly yLabel: string;
  readonly title: string;
  readonly desc: string;
  /** Largura util do desenho, em unidades de modelo. */
  readonly width?: number;
  readonly height?: number;
}

/**
 * Histograma.
 *
 * Convenções que valem a pena registrar, porque são as que distinguem um gráfico
 * legível de um gráfico apenas correto:
 *
 *  - **Sem números sobre as barras.** A frequência já está no eixo vertical; repeti-la
 *    no topo de cada coluna duplica a informação e, num item cujo construto é LER o
 *    gráfico, entrega de graça o que se queria medir.
 *  - **Títulos fora do caminho.** O do eixo vertical fica ao lado dos valores, girado
 *    90°; o do horizontal, abaixo dos valores; o do gráfico, acima de tudo. Nenhum
 *    deles cruza coluna ou número.
 *  - **Corte no eixo x** quando as classes não começam em zero: um VÃO entre o eixo y
 *    e a primeira coluna, com as barras duplas de interrupção dentro dele. Sem o vão,
 *    a marca de corte caía sobre a origem, e a primeira coluna parecia nascer no zero.
 *  - **Escala de 2 em 2, sem linha-guia.** As frequências são inteiras: uma coluna
 *    entre duas linhas da grade vale o ímpar do meio. Números extras e linhas-guia para
 *    essas alturas poluíam o eixo.
 *  - **Classe sem frequência: só o "?".** Nenhum retângulo no lugar da coluna.
 *  - **Colunas de mesma largura.** Classes de amplitudes diferentes mudam o que a
 *    ÁREA da barra significa e transformam o item noutro, mais avançado.
 */
export function histogram(opts: HistogramOptions): Scene {
  const W = opts.width ?? 320;
  const H = opts.height ?? 180;

  const classes = opts.classes;
  const xMin = classes[0]?.lo ?? 0;
  const xMax = classes[classes.length - 1]?.hi ?? 1;
  // Só as classes conhecidas decidem a escala: a apagada não pode denunciar o seu
  // tamanho pelo topo do eixo.
  const fMax = Math.max(...classes.filter((c) => !c.unknown).map((c) => c.freq), 1);

  // Eixo y de 2 em 2, até o par que cobre a maior frequência. Uma coluna de altura ímpar
  // fica entre duas linhas da grade e se lê como o ímpar do meio. De 1 em 1, ou com
  // linhas-guia e números extras para as alturas ímpares, o eixo ficava poluído.
  const yStep = 2;
  const yTop = Math.max(4, Math.ceil(fMax / yStep) * yStep);

  // O VÃO antes da primeira coluna, quando as classes não começam em zero. É nele que
  // moram as barras de interrupção do eixo.
  const corte = xMin > 0;
  const vao = corte ? Math.round(W * 0.1) : 0;
  const direita = vao + W;

  const sx = (x: number): number => vao + ((x - xMin) / (xMax - xMin)) * W;
  const sy = (f: number): number => (f / yTop) * H;

  const shapes: Shape[] = [];

  // Grade horizontal discreta e os números da escala.
  for (let f = yStep; f <= yTop + 1e-9; f += yStep) {
    shapes.push({
      t: 'seg',
      a: { x: 0, y: sy(f) },
      b: { x: direita, y: sy(f) },
      role: 'auxiliary',
    });
    shapes.push({
      t: 'label',
      at: { x: -4, y: sy(f) },
      text: [{ t: 'text', s: formatBR(f, 0) }],
      anchor: 'w',
      emphasis: 'tick',
    });
  }

  // barras
  for (const c of classes) {
    const x0 = sx(c.lo);
    const x1 = sx(c.hi);

    if (c.unknown) {
      // Só o "?", junto do eixo x, no meio da classe. Um retângulo claro — mesmo
      // tracejado — era lido como a própria coluna, com a altura dele; e um "?" no meio
      // da altura cairia numa linha da grade e sugeriria um valor.
      shapes.push({
        t: 'label',
        at: { x: (x0 + x1) / 2, y: H * 0.09 },
        text: [{ t: 'text', s: '?' }],
        anchor: 'c',
        emphasis: 'incognita',
      });
      continue;
    }

    shapes.push({
      t: 'rect',
      at: { x: x0, y: 0, w: x1 - x0, h: sy(c.freq) },
      role: 'given',
      fill: 'result-fill',
    });
    // Sem número no topo: a frequência já está no eixo vertical.
  }

  // eixos
  shapes.push({
    t: 'seg',
    a: { x: 0, y: 0 },
    b: { x: direita + W * 0.04, y: 0 },
    role: 'given',
    arrow: 'end',
  });
  shapes.push({
    t: 'seg',
    a: { x: 0, y: 0 },
    b: { x: 0, y: H * 1.08 },
    role: 'given',
    arrow: 'end',
  });

  // marcas do eixo x nos limites de classe
  const bounds = [...classes.map((c) => c.lo), xMax];
  for (const b of bounds) {
    shapes.push({
      t: 'seg',
      a: { x: sx(b), y: 0 },
      b: { x: sx(b), y: -4 },
      role: 'auxiliary',
    });
    shapes.push({
      t: 'label',
      at: { x: sx(b), y: -8 },
      text: [{ t: 'text', s: formatBR(b, 0) }],
      anchor: 's',
      emphasis: 'tick',
    });
  }

  // CORTE NO EIXO: as barras duplas ficam NO MEIO DO VÃO, entre a origem e a primeira
  // coluna — e o zero aparece na origem, para o eixo dizer "começa no zero, e aqui
  // foi interrompido até a primeira classe".
  if (corte) {
    const xc = vao / 2;
    const s = 5;
    for (const dx of [-3, 3]) {
      shapes.push({
        t: 'seg',
        a: { x: xc + dx - s * 0.5, y: -s },
        b: { x: xc + dx + s * 0.5, y: s },
        role: 'given',
      });
    }
    shapes.push({
      t: 'label',
      at: { x: 0, y: -8 },
      text: [{ t: 'text', s: '0' }],
      anchor: 's',
      emphasis: 'tick',
    });
  }

  // Título do eixo horizontal ABAIXO dos valores, centralizado sob as colunas. Os dois
  // títulos ficam longe dos números: as letras têm tamanho de tela, e num celular a
  // figura encolhe sem que elas encolham (a −48 e a −42, eles se sobrepunham).
  shapes.push({
    t: 'label',
    at: { x: vao + W / 2, y: -66 },
    text: [{ t: 'text', s: opts.xLabel }],
    anchor: 'c',
    emphasis: 'axis',
  });

  // Título do eixo vertical AO LADO dos valores, girado 90°.
  shapes.push({
    t: 'label',
    at: { x: -56, y: H / 2 },
    text: [{ t: 'text', s: opts.yLabel }],
    anchor: 'c',
    rotate: 90,
    emphasis: 'axis',
  });

  // Título do gráfico acima de tudo, sem cruzar coluna nem seta.
  shapes.push({
    t: 'label',
    at: { x: direita / 2, y: H * 1.08 + 18 },
    text: [{ t: 'text', s: opts.title }],
    anchor: 'c',
    emphasis: 'title',
  });

  return scene(shapes, { a11y: { title: opts.title, desc: opts.desc } });
}

export interface BarrasOptions {
  /** Rótulo de cada coluna, na ordem: "Jan", "Fev"… */
  readonly categorias: readonly string[];
  /** Altura de cada coluna; `null` = coluna ainda sem valor (tracejada, com "?"). */
  readonly valores: readonly (number | null)[];
  /** Coluna em destaque — a que o seletor controla. */
  readonly destaque?: number;
  readonly yTop: number;
  /**
   * Onde o eixo vertical COMEÇA. Zero, por padrão — e é o que a honestidade pede.
   * Um valor maior corta a base das colunas e exagera as diferenças: é o gráfico que
   * jornal e propaganda publicam, e o objeto do item do eixo truncado. Quando não é
   * zero, a figura ganha a marca de interrupção no eixo, para o corte ficar declarado.
   */
  readonly yMin?: number;
  /** Passo dos NÚMEROS do eixo vertical. */
  readonly yStep: number;
  /** Passo das linhas de grade; as intermediárias ficam sem número. Padrão: `yStep`. */
  readonly gridStep?: number;
  readonly xLabel: string;
  readonly yLabel: string;
  readonly title: string;
  readonly desc: string;
  readonly width?: number;
  readonly height?: number;
}

/**
 * Gráfico de colunas por categoria — as mesmas convenções do histograma, com uma
 * diferença que importa: as colunas ficam SEPARADAS, porque as categorias não são
 * intervalos contíguos de uma variável, e colunas coladas diriam o contrário.
 *
 *  - Linhas de grade a cada `gridStep`, números a cada `yStep`: o aluno lê "35" numa
 *    linha sem número, entre 30 e 40, como se lê numa régua.
 *  - A coluna em destaque (a do seletor) ganha LINHA-GUIA com o valor no eixo quando
 *    ele não cai numa marca numerada — é a leitura exata do que o aluno escolheu.
 *  - Enquadramento FIXO: com o seletor mexendo na altura de uma coluna, um viewBox
 *    calculado do conteúdo faria a figura inteira pular a cada movimento.
 */
export function barras(opts: BarrasOptions): Scene {
  const W = opts.width ?? 320;
  const H = opts.height ?? 180;
  const n = opts.categorias.length;
  const slot = W / n;
  const larg = slot * 0.56;
  const yMin = opts.yMin ?? 0;
  const sy = (v: number): number => ((v - yMin) / (opts.yTop - yMin)) * H;
  const passoGrade = opts.gridStep ?? opts.yStep;
  const folgaRotulo = H * 0.075;
  const d = opts.destaque;
  const vd = d === undefined ? null : (opts.valores[d] ?? null);
  const guia = vd !== null && vd > 0 && vd % opts.yStep !== 0 ? vd : null;
  const inicio = (i: number): number => i * slot + (slot - larg) / 2;

  const shapes: Shape[] = [];

  for (let k = Math.floor(yMin / passoGrade) + 1; k * passoGrade <= opts.yTop + 1e-9; k++) {
    const v = k * passoGrade;
    shapes.push({ t: 'seg', a: { x: 0, y: sy(v) }, b: { x: W, y: sy(v) }, role: 'auxiliary' });
    const numerado = Math.abs(v / opts.yStep - Math.round(v / opts.yStep)) < 1e-9;
    if (!numerado) continue;
    // O número da guia é mais preciso; o da escala, colado a ele, sai.
    if (guia !== null && Math.abs(sy(guia) - sy(v)) < folgaRotulo) continue;
    shapes.push({
      t: 'label',
      at: { x: -4, y: sy(v) },
      text: [{ t: 'text', s: formatBR(v, 0) }],
      anchor: 'w',
      emphasis: 'tick',
    });
  }

  opts.categorias.forEach((cat, i) => {
    const x0 = inicio(i);
    const v = opts.valores[i] ?? null;
    const emDestaque = i === d;
    if (v === null) {
      // Só o "?": um retângulo claro no lugar da coluna era lido como a própria coluna.
      shapes.push({
        t: 'label',
        at: { x: x0 + larg / 2, y: H * 0.09 },
        text: [{ t: 'text', s: '?' }],
        anchor: 'c',
        emphasis: 'incognita',
      });
    } else if (v > yMin) {
      shapes.push({
        t: 'rect',
        at: { x: x0, y: 0, w: larg, h: sy(v) },
        role: emDestaque ? 'highlight' : 'given',
        fill: 'result-fill',
      });
    }
    shapes.push({
      t: 'label',
      at: { x: i * slot + slot / 2, y: -8 },
      text: [{ t: 'text', s: cat }],
      anchor: 's',
      emphasis: emDestaque ? 'axis' : 'tick',
    });
  });

  if (guia !== null && d !== undefined) {
    shapes.push({
      t: 'seg',
      a: { x: 0, y: sy(guia) },
      b: { x: inicio(d), y: sy(guia) },
      role: 'guide',
    });
    shapes.push({
      t: 'label',
      at: { x: -4, y: sy(guia) },
      text: [{ t: 'text', s: formatBR(guia, 0) }],
      anchor: 'w',
      emphasis: 'tick',
    });
  }

  if (yMin > 0) {
    // Duas barras inclinadas sobre o eixo vertical: o mesmo sinal do histograma, e o
    // número de onde a escala recomeça, para o corte não passar despercebido.
    const yc = H * 0.045;
    for (const dy of [-3, 3]) {
      shapes.push({
        t: 'seg',
        a: { x: -5, y: yc + dy - 2.5 },
        b: { x: 5, y: yc + dy + 2.5 },
        role: 'given',
      });
    }
    // O número do corte fica onde ele de fato está: na base do eixo. E só entra se o
    // primeiro número da escala não estiver colado nele — dois números encavalados no
    // pé do eixo custam mais leitura do que o corte declarado ganha. Sem ele, a marca
    // de interrupção continua avisando que o eixo não começa em zero.
    const primeiro = (Math.floor(yMin / opts.yStep) + 1) * opts.yStep;
    if (sy(primeiro) > folgaRotulo) {
      shapes.push({
        t: 'label',
        at: { x: -4, y: 0 },
        text: [{ t: 'text', s: formatBR(yMin, 0) }],
        anchor: 'w',
        emphasis: 'tick',
      });
    }
  }

  shapes.push({
    t: 'seg',
    a: { x: 0, y: 0 },
    b: { x: W + W * 0.04, y: 0 },
    role: 'given',
    arrow: 'end',
  });
  shapes.push({
    t: 'seg',
    a: { x: 0, y: 0 },
    b: { x: 0, y: H * 1.08 },
    role: 'given',
    arrow: 'end',
  });
  // O título do eixo x vai bem abaixo dos nomes das categorias: o texto tem tamanho de
  // TELA, e num celular a figura encolhe mas as letras não — a −40 ele caía em cima de
  // "Vôlei" e "Basquete".
  shapes.push({
    t: 'label',
    at: { x: W / 2, y: -64 },
    text: [{ t: 'text', s: opts.xLabel }],
    anchor: 'c',
    emphasis: 'axis',
  });
  shapes.push({
    t: 'label',
    at: { x: -54, y: H / 2 },
    text: [{ t: 'text', s: opts.yLabel }],
    anchor: 'c',
    rotate: 90,
    emphasis: 'axis',
  });
  shapes.push({
    t: 'label',
    at: { x: W / 2, y: H * 1.08 + 18 },
    text: [{ t: 'text', s: opts.title }],
    anchor: 'c',
    emphasis: 'title',
  });

  const viewBox = { x: -74, y: -80, w: W + 74 + W * 0.08, h: H * 1.08 + 32 + 80 };
  return scene(shapes, { a11y: { title: opts.title, desc: opts.desc }, viewBox });
}

/** Descricao textual de um histograma -- o `alt` obrigatorio, gerado dos dados. */
export function describeHistogram(classes: readonly HistogramClass[], grandeza: string): string {
  const partes = classes.map((c) =>
    c.unknown ? `de ${c.lo} a ${c.hi}: não informada` : `de ${c.lo} a ${c.hi}: ${c.freq}`,
  );
  return `Histograma ${de(grandeza)}. Classes e frequências — ${partes.join('; ')}.`;
}

// ---------------------------------------------------------------------------
// Grafico de setores
// ---------------------------------------------------------------------------

export interface Fatia {
  readonly nome: string;
  /** A parte, na unidade que o item usar: pessoas, votos, reais. */
  readonly valor: number;
}

export interface SetoresOptions {
  readonly fatias: readonly Fatia[];
  /** Qual fatia recebe preenchimento e, se pedido, a marca de angulo. */
  readonly destaque?: number;
  /** O que vai escrito ao lado do nome de cada fatia. */
  readonly rotulo?: 'nada' | 'percentual' | 'angulo' | 'valor';
  /** Escreve o angulo, em graus, dentro da fatia em destaque. */
  readonly anguloNoDestaque?: boolean;
  readonly title: string;
  readonly desc: string;
  readonly raio?: number;
}

/**
 * GRAFICO DE SETORES.
 *
 * Convencoes, e por que elas:
 *
 *  - **Sem cor por fatia.** As figuras da casa sao monocromaticas e a cor esta reservada
 *    para significado — dado, construcao, resposta, erro. Um grafico de setores em seis
 *    cores obrigaria a inventar uma paleta que nao quer dizer nada, e depois a defende-la
 *    em daltonismo. Quem identifica a fatia e o **rotulo ao lado dela**, que e como se le
 *    um grafico de setores impresso em preto e branco.
 *  - **Comeca as 12 horas e anda no sentido horario**, que e a ordem em que se le um
 *    relogio e um grafico de setores em jornal.
 *  - **O setor em destaque e o unico preenchido.** Ele e o objeto do item; o resto e
 *    contexto, e contexto nao disputa atencao.
 *  - **O angulo, quando aparece, e escrito e nao so desenhado.** O construto do item de
 *    setores e a relacao angulo-proporcao, e essa relacao so se usa com o numero na mao.
 *  - **Os rotulos ficam fora do circulo.** Dentro, eles cabem na fatia grande e nao
 *    cabem na pequena, e a figura passaria a mentir sobre qual fatia importa.
 */
export function setores(opts: SetoresOptions): Scene {
  const R = opts.raio ?? 100;
  const fatias = opts.fatias;
  const total = fatias.reduce((s, f) => s + f.valor, 0);
  const shapes: Shape[] = [];

  const rad = (g: number): number => (g * Math.PI) / 180;
  const ponto = (g: number, r: number): Vec2 => ({
    x: r * Math.cos(rad(g)),
    y: r * Math.sin(rad(g)),
  });

  /** Onde cada fatia comeca e termina, em graus, das 12 horas e no sentido horario. */
  let a = 90;
  const arcos = fatias.map((f) => {
    const abertura = (f.valor / total) * 360;
    const inicio = a;
    a -= abertura;
    return { inicio, fim: a, abertura };
  });

  arcos.forEach((arco, i) => {
    // O setor e um poligono fechado: o centro, o arco em passos de 2 graus, e de volta.
    // Nao ha forma "setor" na cena, e inventar uma so para isto custaria mais do que
    // ganha — o poligono ja herda tema, impressao e teste de cena.
    const pts: Vec2[] = [{ x: 0, y: 0 }];
    const passos = Math.max(2, Math.ceil(Math.abs(arco.abertura) / 2));
    for (let k = 0; k <= passos; k++)
      pts.push(ponto(arco.inicio - (Math.abs(arco.abertura) * k) / passos, R));
    shapes.push({
      t: 'poly',
      pts,
      closed: true,
      role: i === opts.destaque ? 'result' : 'given',
      fill: i === opts.destaque ? 'result-fill' : 'none',
    });
  });

  // Os rotulos, fora do circulo, no meio de cada fatia.
  arcos.forEach((arco, i) => {
    const fatia = fatias[i] as Fatia;
    const meio = arco.inicio - Math.abs(arco.abertura) / 2;
    const cosMeio = Math.cos(rad(meio));
    // Em cima e embaixo o rótulo não tem para onde crescer sem passar pelo círculo:
    // ali ele sobe (ou desce) mais um pouco antes de ser escrito.
    const p = ponto(meio, Math.abs(cosMeio) > 0.25 ? R * 1.06 : R * 1.2);
    // O rótulo cresce para FORA do círculo. `anchor` diz de que lado do ponto o texto
    // fica: a leste para as fatias da direita, a oeste para as da esquerda. Com
    // 'c' em todos, um rótulo longo — "ônibus (38%)" — deitava metade do texto sobre
    // a própria fatia.
    const anchor: 'w' | 'e' | 'c' = cosMeio > 0.25 ? 'e' : cosMeio < -0.25 ? 'w' : 'c';
    const extra =
      opts.rotulo === 'percentual'
        ? `\u00a0(${pctTexto(fatia.valor, total)})`
        : opts.rotulo === 'angulo'
          ? `\u00a0(${formatBR(Math.round(arco.abertura), 0)}°)`
          : opts.rotulo === 'valor'
            ? `\u00a0(${formatBR(fatia.valor, 0)})`
            : '';
    shapes.push({
      t: 'label',
      at: p,
      text: [{ t: 'text', s: fatia.nome + extra }],
      anchor,
      emphasis: 'tick',
    });
  });

  if (opts.anguloNoDestaque && opts.destaque !== undefined) {
    const arco = arcos[opts.destaque];
    if (arco) {
      const meio = arco.inicio - Math.abs(arco.abertura) / 2;
      shapes.push({
        t: 'label',
        at: ponto(meio, R * 0.55),
        text: [{ t: 'text', s: `${formatBR(Math.round(arco.abertura), 0)}°` }],
        anchor: 'c',
        emphasis: 'tick',
      });
    }
  }

  shapes.push({ t: 'circle', c: { x: 0, y: 0 }, r: R, role: 'given' });

  shapes.push({
    t: 'label',
    at: { x: 0, y: R * 1.62 },
    text: [{ t: 'text', s: opts.title }],
    anchor: 'c',
    emphasis: 'title',
  });

  // A caixa é larga: o rótulo sai do círculo e cresce para fora, e num gráfico de
  // setores o nome da fatia é o que identifica a fatia — cortá-lo é perder o dado.
  const viewBox = { x: -R * 2.2, y: -R * 1.62 - 20, w: R * 4.4, h: R * 3.24 + 46 };
  return scene(shapes, { a11y: { title: opts.title, desc: opts.desc }, viewBox });
}

/** Sem casa decimal quando a fatia da um percentual inteiro: "38%", e nao "38,0%". */
function pctTexto(valor: number, total: number): string {
  const x = Math.round((valor / total) * 1000) / 10;
  return `${formatBR(x, Number.isInteger(x) ? 0 : 1)}%`;
}

/** Descricao textual de um grafico de setores -- o `alt` obrigatorio, gerado dos dados. */
export function describeSetores(
  fatias: readonly Fatia[],
  grandeza: string,
  comAngulo = false,
): string {
  const total = fatias.reduce((s, f) => s + f.valor, 0);
  const partes = fatias.map((f) => {
    const pct = pctTexto(f.valor, total);
    const ang = formatBR(Math.round((f.valor / total) * 360), 0);
    return comAngulo ? `${f.nome}: ${pct} (${conta(ang, 'grau')})` : `${f.nome}: ${pct}`;
  });
  return `Gráfico de setores ${de(grandeza)}. Setores — ${partes.join('; ')}.`;
}

// ---------------------------------------------------------------------------
// Plano cartesiano
// ---------------------------------------------------------------------------

export interface CartesianOptions {
  /** Semi-extensao dos eixos, em unidades do modelo. */
  readonly span: number;
  readonly step: number;
  readonly title: string;
  readonly desc: string;
  /** Escala de tela: unidades de desenho por unidade matematica. */
  readonly unidade?: number;
  /** Numerar as marcas dos eixos. Padrão: sim. */
  readonly numerar?: boolean;
}

/** Eixos com marcas e malha pontilhada. Base comum aos itens do plano cartesiano. */
export function cartesianFrame(opts: CartesianOptions): Shape[] {
  const u = opts.unidade ?? 18;
  const R = opts.span * u;
  const extent = { x: -R, y: -R, w: 2 * R, h: 2 * R };
  // Os eixos passam um pouco da malha: a última marca (±span) fica livre da ponta da
  // seta e do nome do eixo — e é justamente ela que leva número.
  const E = R + 0.8 * u;
  const eixos = { x: -E, y: -E, w: 2 * E, h: 2 * E };

  return [
    { t: 'grid', step: opts.step * u, extent, dots: false },
    { t: 'axes', extent: eixos, step: opts.step * u, xLabel: 'x', yLabel: 'y' },
  ];
}

/**
 * NÚMEROS NOS EIXOS — só nas extremidades e na origem.
 *
 * Sem número nenhum, decidir qual gráfico é o de f(x) = −2x + 3 vira palpite: a reta
 * passa "mais ou menos" no terceiro pontinho, e nada garante que seja o terceiro.
 * Com TODAS as marcas numeradas, o palpite some — mas vinte e poucos números em cada
 * uma das cinco alternativas poluem a figura, e a reta passa a disputar espaço com
 * eles.
 *
 * O meio-termo é o dos livros: numerar a origem e as marcas extremas (−6 e 6, por
 * exemplo), manter TODAS as marcas e a malha de pontos. O aluno vê a escala nas
 * pontas e conta as marcas a partir do zero — ler o intercepto e a inclinação
 * continua sendo leitura, sem o ruído.
 *
 * Os números vão por CIMA de tudo, com o halo da cor do fundo: uma reta que passa
 * sobre um número abre um vão para ele, em vez de riscá-lo.
 */
export function numerosDosEixos(opts: CartesianOptions): Shape[] {
  if (opts.numerar === false) return [];
  const u = opts.unidade ?? 18;
  const extremo = Math.floor(opts.span / opts.step + 1e-9) * opts.step;
  const formas: Shape[] = [];

  for (const v of [-extremo, extremo]) {
    const texto = [{ t: 'text' as const, s: formatBR(v) }];
    formas.push({ t: 'label', at: { x: v * u, y: 0 }, text: texto, anchor: 's', emphasis: 'tick' });
    formas.push({ t: 'label', at: { x: 0, y: v * u }, text: texto, anchor: 'w', emphasis: 'tick' });
  }
  formas.push({
    t: 'label',
    at: { x: 0, y: 0 },
    text: [{ t: 'text', s: '0' }],
    anchor: 'sw',
    emphasis: 'tick',
  });
  return formas;
}

/** Grafico de uma funcao afim y = ax + b, recortado ao quadro. */
export function afimScene(
  a: number,
  b: number,
  opts: CartesianOptions & { readonly destaque?: boolean },
): Scene {
  const u = opts.unidade ?? 18;
  const S = opts.span;

  // Recorta a reta no quadrado [-S, S]^2 pelos pontos de saida, para a reta nunca
  // escapar da moldura nem terminar no ar dentro dela.
  const cand: Vec2[] = [];
  for (const x of [-S, S]) {
    const y = a * x + b;
    if (Math.abs(y) <= S + 1e-9) cand.push({ x, y });
  }
  if (Math.abs(a) > 1e-9) {
    for (const y of [-S, S]) {
      const x = (y - b) / a;
      if (Math.abs(x) <= S + 1e-9) cand.push({ x, y });
    }
  }

  const shapes: Shape[] = cartesianFrame(opts);

  if (cand.length >= 2) {
    const p0 = cand[0] as Vec2;
    let p1 = cand[1] as Vec2;
    // pega o par mais distante, para nao desenhar um tico de reta
    for (const c of cand.slice(1)) {
      if (Math.hypot(c.x - p0.x, c.y - p0.y) > Math.hypot(p1.x - p0.x, p1.y - p0.y)) p1 = c;
    }
    shapes.push({
      t: 'seg',
      a: { x: p0.x * u, y: p0.y * u },
      b: { x: p1.x * u, y: p1.y * u },
      role: opts.destaque === false ? 'given' : 'result',
    });
  }

  // Intercepto marcado: e o elemento que o aluno precisa ler para decidir.
  if (Math.abs(b) <= S) {
    shapes.push({ t: 'point', p: { x: 0, y: b * u }, role: 'derived' });
  }

  // Os números por último: por cima da reta, que abre vão para eles.
  shapes.push(...numerosDosEixos(opts));

  return scene(shapes, { a11y: { title: opts.title, desc: opts.desc } });
}

/** Descricao textual de uma reta afim — o `alt` obrigatorio, gerado dos parametros. */
export function describeAfim(a: number, b: number): string {
  const sentido = a > 0 ? 'crescente' : 'decrescente';
  // Inclinação em fração, não em decimal: é a notação do livro didático.
  const incl = Number.isInteger(a) ? formatBR(a) : fracaoSimples(a);
  const corte = Math.abs(b) < 1e-9 ? 'passando pela origem' : `cortando o eixo y em ${formatBR(b)}`;
  return `Reta ${sentido}, ${corte}, com inclinação ${incl}.`;
}

// ---------------------------------------------------------------------------
// Poligono com diagonais
// ---------------------------------------------------------------------------

/** Poligono regular de n lados com todas as diagonais tracadas. */
export function poligonoComDiagonais(
  n: number,
  opts: { readonly raio?: number; readonly title: string; readonly desc: string },
): Scene {
  const R = opts.raio ?? 90;
  const vertices: Vec2[] = [];
  // Comeca em -90 graus para o poligono ficar apoiado, nao girado ao acaso.
  for (let i = 0; i < n; i++) {
    const ang = (2 * Math.PI * i) / n - Math.PI / 2;
    vertices.push({ x: R * Math.cos(ang), y: R * Math.sin(ang) });
  }

  const shapes: Shape[] = [];

  // Diagonais primeiro, para os lados ficarem por cima e o contorno ler-se claro.
  for (let i = 0; i < n; i++) {
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue; // esse par e um lado, nao diagonal
      shapes.push({
        t: 'seg',
        a: vertices[i] as Vec2,
        b: vertices[j] as Vec2,
        role: 'result',
      });
    }
  }

  shapes.push({ t: 'poly', pts: vertices, closed: true, role: 'given' });
  for (const v of vertices) shapes.push({ t: 'point', p: v, role: 'given' });

  return scene(shapes, { a11y: { title: opts.title, desc: opts.desc } });
}

/** Diagrama de percurso em trechos, usado no item de velocidade media. */
export interface TripLeg {
  readonly distancia: number;
  readonly velocidade: number;
  readonly rotulo: string;
}

export function tripDiagram(
  legs: readonly TripLeg[],
  progresso: number,
  opts: { title: string; desc: string; width?: number },
): Scene {
  const W = opts.width ?? 340;
  const total = legs.reduce((s, l) => s + l.distancia, 0);
  const y = 0;
  const shapes: Shape[] = [];

  let acc = 0;
  const marcos: Vec2[] = [{ x: 0, y }];

  // TUDO O QUE É FIXO FICA ABAIXO DA ESTRADA: a cota com a distância e, logo embaixo,
  // a velocidade do trecho — "120 km / a 60 km/h". Acima da estrada só anda o móvel,
  // com o seu rótulo. Antes a velocidade ficava em cima, na mesma altura de "o móvel",
  // e a cada passagem pelo meio de um trecho um texto atropelava o outro.
  for (const leg of legs) {
    const x0 = (acc / total) * W;
    acc += leg.distancia;
    const x1 = (acc / total) * W;

    shapes.push({ t: 'seg', a: { x: x0, y }, b: { x: x1, y }, role: 'given' });
    marcos.push({ x: x1, y });

    shapes.push({
      t: 'dimension',
      a: { x: x0, y },
      b: { x: x1, y },
      offset: -22,
      label: [{ t: 'text', s: `${formatBR(leg.distancia, 0)} km` }],
    });
    shapes.push({
      t: 'label',
      at: { x: (x0 + x1) / 2, y: y - 44 },
      text: [{ t: 'text', s: `a ${formatBR(leg.velocidade, 0)} km/h` }],
      anchor: 'c',
      role: 'result',
    });
  }

  for (const m of marcos) shapes.push({ t: 'point', p: m, role: 'given' });

  // O MÓVEL. Ganha rótulo porque a página tem outra bolinha que também anda — a do
  // controle de tempo — e sem nome o aluno não sabe qual das duas o texto comenta.
  const xMovel = Math.max(0, Math.min(1, progresso)) * W;
  shapes.push({ t: 'circle', c: { x: xMovel, y }, r: 7, role: 'result' });
  shapes.push({ t: 'point', p: { x: xMovel, y }, role: 'result' });
  shapes.push({
    t: 'label',
    at: { x: xMovel, y: y + 15 },
    text: [{ t: 'text', s: 'o móvel' }],
    anchor: 'c',
    role: 'result',
  });

  // ENQUADRAMENTO FIXO, calculado do percurso inteiro e não do quadro corrente.
  // Sem isto, os limites da cena mudavam quando o móvel se aproximava da borda: a
  // figura encolhia sozinha no fim da animação, como se recuasse.
  const folgaX = 26;
  const viewBox = {
    x: -folgaX,
    y: y - 58,
    w: W + 2 * folgaX,
    h: 88,
  };

  return scene(shapes, { viewBox, a11y: { title: opts.title, desc: opts.desc } });
}

/** Fração de denominador pequeno, em texto corrido: "1/2", "-3/4". */
function fracaoSimples(x: number): string {
  const sinal = x < 0 ? '−' : '';
  const m = Math.abs(x);
  for (let d = 2; d <= 6; d++) {
    const n = m * d;
    if (Math.abs(n - Math.round(n)) < 1e-9) return `${sinal}${Math.round(n)}/${d}`;
  }
  return formatBR(x, 2);
}
